"use client";

/**
 * SignalTradeChart — candlestick chart for a single signal leg.
 *
 * Reuses the blog/top-trade `lightweight-charts` pattern: CandlestickSeries +
 * markers + horizontal price lines for entry / take-profit / stop. Fed by the
 * existing /api/polygon/aggregates route (hourly OHLC).
 *
 * autoFit mode (used in mock): the price feed for this key returns synthetic
 * prices that won't match hand-written mock levels, so we DERIVE entry / TP /
 * stop / current from the actual candles and report them back via `onLevels`
 * so the card shows numbers that match the chart. In production (real data),
 * pass autoFit={false} and the real levels render as-is.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  createSeriesMarkers,
  type UTCTimestamp,
  type IChartApi,
  type CandlestickData,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";

type RangeKey = "60d" | "14d" | "7d" | "1d";
const RANGE_DAYS: Record<RangeKey, number> = { "60d": 60, "14d": 14, "7d": 7, "1d": 1 };

// Trade-anchored selectors (for signal trade charts). Unlike the "now-anchored"
// RANGE_DAYS above, these frame the actual trade: the window's right edge is the
// trade's reference end (exit for closed trades, now for open ones), so the
// trade is always on-screen. "trade" fits the whole entry→exit window.
type TradeRangeKey = "trade" | "1m" | "3m" | "6m";
const TRADE_RANGE_DAYS: Record<Exclude<TradeRangeKey, "trade">, number> = {
  "1m": 30,
  "3m": 90,
  "6m": 180,
};
const TRADE_RANGE_ORDER: TradeRangeKey[] = ["trade", "1m", "3m", "6m"];
const TRADE_RANGE_LABELS: Record<TradeRangeKey, string> = {
  trade: "Trade",
  "1m": "1M",
  "3m": "3M",
  "6m": "6M",
};
const EMPTY_REFERENCE_LINES: ReferenceLine[] = [];

export interface DerivedLevels {
  entry: number;
  current: number;
  target: number;
  stop: number;
}

export interface ReferenceLine {
  price: number;
  title: string;
  color: string;
}

export interface SignalTradeChartProps {
  ticker: string;
  label?: string;
  direction?: "long" | "short";
  entryTs: number; // unix seconds
  exitTs?: number; // unix seconds (closed trades)
  /** Explicit levels (used when autoFit is false / real data). */
  entry?: number;
  stop?: number;
  target?: number;
  exitPrice?: number;
  /** Derive levels from real candles and report back (mock mode). */
  autoFit?: boolean;
  onLevels?: (l: DerivedLevels) => void;
  /** Now-anchored range buttons (1D/7D/14D/60D from today). For live contracts. */
  rangeSelector?: boolean;
  /** Trade-anchored range buttons (Trade/1M/3M/6M, framed on the trade). */
  tradeRangeSelector?: boolean;
  showTradeLevels?: boolean;
  referenceLines?: ReferenceLine[];
  fromSec?: number;
  toSec?: number;
  height?: number;
  /** Candle granularity. Hourly keeps signal entry/exit markers precise. */
  timespan?: "hour" | "day";
  /** Underlying root for option contracts — enables the live-quote fallback
   * when an illiquid contract has zero traded bars. */
  underlyingTicker?: string;
  /** Render style. Thin option contracts read better as a clean line of the
   * close than as sparse candlesticks. Defaults to candlestick. */
  seriesType?: "candlestick" | "line";
}

interface QuoteFallback {
  bid: number | null;
  ask: number | null;
  mid: number | null;
  last_trade: number | null;
  day_close: number | null;
  open_interest: number | null;
}

export function SignalTradeChart({
  ticker,
  label,
  direction = "long",
  entryTs,
  exitTs,
  entry,
  stop,
  target,
  exitPrice,
  autoFit = false,
  onLevels,
  rangeSelector = false,
  tradeRangeSelector = false,
  showTradeLevels = true,
  referenceLines = EMPTY_REFERENCE_LINES,
  fromSec,
  toSec,
  height = 360,
  timespan = "hour",
  underlyingTicker,
  seriesType = "candlestick",
}: SignalTradeChartProps) {
  const [range, setRange] = useState<RangeKey>("14d");
  const [tradeRange, setTradeRange] = useState<TradeRangeKey>("trade");
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const onLevelsRef = useRef(onLevels);
  onLevelsRef.current = onLevels;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteFallback | null>(null);

  const isOptionContract = ticker.startsWith("O:");
  // Underlying root: explicit prop, else parsed from the OCC symbol.
  const underlyingRoot = useMemo(() => {
    if (underlyingTicker) return underlyingTicker;
    const m = ticker.match(/^O:([A-Z0-9]+?)\d{6}[CP]\d{8}$/);
    return m ? m[1] : null;
  }, [ticker, underlyingTicker]);

  const { winFrom, winTo, effTimespan } = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);

    // Trade-anchored selectors: right edge = trade's reference end (exit for
    // closed, now for open) so the trade always stays on-screen.
    if (tradeRangeSelector) {
      const refEnd = toSec ?? (exitTs ? exitTs + 7 * 86400 : now);
      if (tradeRange === "trade") {
        // Fit the whole trade. Hourly: with limit=50000 even a multi-month
        // window returns in full, so markers stay precise.
        return { winFrom: fromSec ?? entryTs - 7 * 86400, winTo: refEnd, effTimespan: "hour" as const };
      }
      const days = TRADE_RANGE_DAYS[tradeRange];
      return {
        winFrom: refEnd - days * 86400,
        winTo: refEnd,
        effTimespan: (days <= 30 ? "hour" : "day") as "hour" | "day",
      };
    }

    if (rangeSelector) {
      return { winFrom: now - RANGE_DAYS[range] * 86400, winTo: now, effTimespan: timespan };
    }
    const a = fromSec ?? entryTs - 7 * 86400;
    const b = toSec ?? (exitTs ?? entryTs) + 7 * 86400;
    return { winFrom: a, winTo: b, effTimespan: timespan };
  }, [rangeSelector, tradeRangeSelector, range, tradeRange, fromSec, toSec, entryTs, exitTs, timespan]);

  useEffect(() => {
    if (!containerRef.current) return;
    setLoading(true);
    setError(null);
    setQuote(null);

    const fromDate = new Date(winFrom * 1000).toISOString().split("T")[0];
    const toDate = new Date(winTo * 1000).toISOString().split("T")[0];
    const url = `/api/polygon/aggregates?ticker=${encodeURIComponent(
      ticker,
    )}&from=${fromDate}&to=${toDate}&timespan=${effTimespan}&multiplier=1`;

    let cancelled = false;
    let ro: ResizeObserver | null = null;

    fetch(url)
      .then((r) => r.json())
      .then((json) => {
        if (cancelled || !containerRef.current) return;
        const results = json.results;
        if (!results || results.length === 0) {
          // Illiquid option contract with zero traded bars: fall back to the
          // live NBBO quote instead of a broken "No price data" chart.
          if (isOptionContract && underlyingRoot) {
            fetch(
              `/api/polygon/option-snapshot?occ=${encodeURIComponent(
                ticker,
              )}&underlying=${encodeURIComponent(underlyingRoot)}`,
            )
              .then((r) => r.json())
              .then((q: QuoteFallback & { error?: string }) => {
                if (cancelled) return;
                if (q && !q.error && (q.mid != null || q.last_trade != null || q.bid != null)) {
                  setQuote(q);
                } else {
                  setError("This contract rarely trades — no recent price data.");
                }
                setLoading(false);
              })
              .catch(() => {
                if (cancelled) return;
                setError("This contract rarely trades — no recent price data.");
                setLoading(false);
              });
            return;
          }
          setError("No price data for this window.");
          setLoading(false);
          return;
        }

        const candles: CandlestickData[] = results.map(
          (r: { t: number; o: number; h: number; l: number; c: number }) => ({
            time: Math.floor(r.t / 1000) as UTCTimestamp,
            open: r.o,
            high: r.h,
            low: r.l,
            close: r.c,
          }),
        );

        const findNearest = (ts: number) => {
          let best = candles[0];
          let bestDiff = Math.abs((best.time as number) - ts);
          for (const c of candles) {
            const diff = Math.abs((c.time as number) - ts);
            if (diff < bestDiff) {
              bestDiff = diff;
              best = c;
            }
          }
          return best;
        };

        const entryCandle = findNearest(entryTs);
        const exitCandle = exitTs ? findNearest(exitTs) : undefined;

        // Resolve the levels to draw.
        let lEntry: number;
        let lTarget: number;
        let lStop: number;
        let lCurrent: number;
        let lExit: number | undefined;

        if (autoFit) {
          lEntry = entryCandle.close;
          lCurrent = candles[candles.length - 1].close;
          lExit = exitCandle ? exitCandle.close : undefined;
          if (direction === "short") {
            lTarget = lEntry * 0.9;
            lStop = lEntry * 1.05;
          } else {
            lTarget = lEntry * 1.1;
            lStop = lEntry * 0.95;
          }
          onLevelsRef.current?.({
            entry: lEntry,
            current: lCurrent,
            target: lTarget,
            stop: lStop,
          });
        } else {
          lEntry = entry ?? entryCandle.close;
          lTarget = target ?? lEntry * 1.1;
          lStop = stop ?? lEntry * 0.95;
          lCurrent = candles[candles.length - 1].close;
          lExit = exitPrice;
        }

        const chart = createChart(containerRef.current, {
          width: containerRef.current.clientWidth,
          height,
          layout: { background: { color: "transparent" }, textColor: "#e2e8f0", fontSize: 11 },
          grid: {
            vertLines: { color: "rgba(148,163,184,0.06)" },
            horzLines: { color: "rgba(148,163,184,0.06)" },
          },
          crosshair: { mode: 0 },
          rightPriceScale: {
            borderColor: "rgba(148,163,184,0.12)",
            borderVisible: true,
            minimumWidth: 56,
          },
          timeScale: {
            borderColor: "rgba(148,163,184,0.12)",
            timeVisible: true,
            rightOffset: 4,
          },
        });
        chartRef.current = chart;

        // Thin option contracts read better as a clean close-line; equities and
        // the underlying keep full candlesticks. Markers + price lines attach to
        // whichever series we create, so downstream code is unchanged.
        const series =
          seriesType === "line"
            ? chart.addSeries(LineSeries, {
                color: "#38bdf8",
                lineWidth: 2,
                priceLineVisible: false,
                lastValueVisible: true,
              })
            : chart.addSeries(CandlestickSeries, {
                upColor: "#22c55e",
                downColor: "#ef4444",
                borderVisible: false,
                wickUpColor: "#22c55e",
                wickDownColor: "#ef4444",
              });
        if (seriesType === "line") {
          series.setData(candles.map((c) => ({ time: c.time, value: c.close })));
        } else {
          series.setData(candles);
        }

        if (showTradeLevels) {
          series.createPriceLine({
            price: lEntry,
            color: "#ffffff",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "Entry",
          });
          series.createPriceLine({
            price: lTarget,
            color: "#22c55e",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "Target",
          });
          series.createPriceLine({
            price: lStop,
            color: "#f87171",
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: "Stop",
          });
        }

        referenceLines.forEach((line) => {
          series.createPriceLine({
            price: line.price,
            color: line.color,
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: true,
            title: line.title,
          });
        });

        const markers: SeriesMarker<Time>[] = [
          {
            time: entryCandle.time,
            position: "belowBar",
            color: "#ffffff",
            shape: "arrowUp",
            text: "Entry",
          },
        ];
        if (exitCandle) {
          markers.push({
            time: exitCandle.time,
            position: "aboveBar",
            color: "#facc15",
            shape: "arrowDown",
            text: "Exit",
          });
        }
        markers.sort((a, b) => (a.time as number) - (b.time as number));
        createSeriesMarkers(series, markers);

        chart.timeScale().fitContent();
        setLoading(false);

        ro = new ResizeObserver(() => {
          if (containerRef.current) {
            chart.applyOptions({ width: containerRef.current.clientWidth });
          }
        });
        ro.observe(containerRef.current);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(`Couldn't load chart: ${e.message}`);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (ro) ro.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [ticker, winFrom, winTo, entry, stop, target, exitPrice, entryTs, exitTs, autoFit, direction, height, showTradeLevels, referenceLines, effTimespan, isOptionContract, underlyingRoot, seriesType]);

  return (
    <div className="relative w-full">
      {rangeSelector && (
        <div className="flex items-center gap-1 mb-2">
          {(Object.keys(RANGE_DAYS) as RangeKey[]).map((k) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                range === k
                  ? "bg-emerald-400/15 border border-emerald-400/40 text-white"
                  : "text-white/60 hover:text-white border border-transparent"
              }`}
            >
              {k.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {tradeRangeSelector && (
        <div className="flex items-center gap-1 mb-2">
          {TRADE_RANGE_ORDER.map((k) => (
            <button
              key={k}
              onClick={() => setTradeRange(k)}
              className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
                tradeRange === k
                  ? "bg-emerald-400/15 border border-emerald-400/40 text-white"
                  : "text-white/60 hover:text-white border border-transparent"
              }`}
            >
              {TRADE_RANGE_LABELS[k]}
            </button>
          ))}
        </div>
      )}
      <div className="relative rounded-lg border border-white/10 bg-black/40 overflow-hidden">
        {label && (
          <div className="absolute top-2.5 left-3 z-10 text-white/70 text-xs font-semibold tracking-wide pointer-events-none">
            {label}
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <Loader2 className="w-4 h-4 animate-spin text-white/50" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10 text-xs text-white/70 px-4 text-center">
            {error}
          </div>
        )}
        {quote && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-1.5 px-4 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-white/45">
              Rarely trades — no candles · live quote
            </div>
            <div className="text-2xl font-semibold text-white">
              {quote.mid != null
                ? `$${quote.mid.toFixed(2)}`
                : quote.last_trade != null
                  ? `$${quote.last_trade.toFixed(2)}`
                  : "—"}
              <span className="ml-1.5 text-xs font-medium text-white/50">
                {quote.mid != null ? "mid" : quote.last_trade != null ? "last" : ""}
              </span>
            </div>
            <div className="text-[11px] text-white/60">
              bid {quote.bid != null ? `$${quote.bid.toFixed(2)}` : "—"} · ask{" "}
              {quote.ask != null ? `$${quote.ask.toFixed(2)}` : "—"}
              {quote.open_interest != null ? ` · OI ${quote.open_interest.toLocaleString()}` : ""}
            </div>
            {entry != null && (
              <div className="text-[11px] text-white/45">entry ${entry.toFixed(2)}</div>
            )}
          </div>
        )}
        <div ref={containerRef} style={{ height }} />
      </div>
    </div>
  );
}
